import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  createComposerSession,
  defaultComposerTargetForRecord,
} from '@command-center/core-platform';
import {
  buildFallbackAliceContext,
  DEFAULT_ALLOWED_ACTIONS,
  DEFAULT_BLOCKED_ACTIONS,
} from './alice.mock';
import { getAliceSuggestions } from './alice.service';
import type {
  AliceComposerSession,
  AliceMode,
  AlicePageContext,
  AlicePanelState,
  AliceSuggestedAction,
  PortalAliceConfig,
} from './alice.types';

interface AliceContextValue {
  config: PortalAliceConfig;
  currentContext: AlicePageContext;
  panelState: AlicePanelState;
  mode: AliceMode;
  suggestions: AliceSuggestedAction[];
  useCurrentContext: boolean;
  composerSession: AliceComposerSession | null;
  composerDraft: string;
  composerDirty: boolean;
  registerAliceContext: (context: AlicePageContext) => () => void;
  setPanelState: (state: AlicePanelState) => void;
  setMode: (mode: AliceMode) => void;
  setUseCurrentContext: (value: boolean) => void;
  setComposerDraft: (value: string) => void;
  discardComposerDraft: () => void;
  runSuggestion: (suggestion: AliceSuggestedAction) => void;
}

const AliceContext = createContext<AliceContextValue | undefined>(undefined);

const DISABLED_ALICE_CONFIG: PortalAliceConfig = {
  enabled: false,
  defaultState: 'hidden',
  assistantName: 'Alice',
  portalScope: 'sandbox',
  allowedContextSources: ['page'],
  allowedActions: DEFAULT_ALLOWED_ACTIONS,
  blockedActions: DEFAULT_BLOCKED_ACTIONS,
  enabledModes: ['global'],
  quickActionsEnabled: false,
  voiceEnabledPlaceholder: false,
};

export function AliceProvider({
  children,
  config,
  portalKey,
}: {
  children: ReactNode;
  config?: PortalAliceConfig;
  portalKey: string;
}) {
  const resolvedConfig = config ?? DISABLED_ALICE_CONFIG;
  const fallbackContext = useMemo(() => buildFallbackAliceContext(portalKey), [portalKey]);
  const [currentContext, setCurrentContext] = useState<AlicePageContext>(fallbackContext);
  const [panelState, setPanelState] = useState<AlicePanelState>(
    resolvedConfig.enabled ? resolvedConfig.defaultState : 'hidden',
  );
  const [mode, setModeState] = useState<AliceMode>(
    resolvedConfig.enabledModes.includes('global') ? 'global' : resolvedConfig.enabledModes[0] ?? 'global',
  );
  const [useCurrentContext, setUseCurrentContext] = useState(true);
  const [composerSession, setComposerSession] = useState<AliceComposerSession | null>(null);
  const composerDraft = composerSession?.draftText ?? '';
  const composerDirty = composerSession?.dirty ?? false;

  useEffect(() => {
    setCurrentContext(fallbackContext);
  }, [fallbackContext]);

  const registerAliceContext = useCallback(
    (context: AlicePageContext) => {
      if (!resolvedConfig.enabled) return () => undefined;

      const allowedSources = new Set(resolvedConfig.allowedContextSources);
      const scopedContext: AlicePageContext = {
        ...context,
        portalKey,
        contextItems: context.contextItems.filter((item) => allowedSources.has(item.sourceType)),
        allowedActions: context.allowedActions.filter((action) =>
          resolvedConfig.allowedActions.includes(action),
        ),
        blockedActions: Array.from(
          new Set([...resolvedConfig.blockedActions, ...context.blockedActions]),
        ),
      };

      setCurrentContext(scopedContext);
      return () => {
        setCurrentContext((current) => (current.pageKey === context.pageKey ? fallbackContext : current));
      };
    },
    [fallbackContext, portalKey, resolvedConfig],
  );

  const setMode = useCallback(
    (nextMode: AliceMode) => {
      if (!resolvedConfig.enabledModes.includes(nextMode)) return;
      setModeState(nextMode);
    },
    [resolvedConfig.enabledModes],
  );

  const suggestions = useMemo(
    () => getAliceSuggestions(currentContext, resolvedConfig),
    [currentContext, resolvedConfig],
  );

  const setComposerDraft = useCallback((value: string) => {
    setComposerSession((current) => {
      if (current) {
        return { ...current, draftText: value, dirty: value.trim().length > 0, status: 'active' };
      }
      return createComposerSession({ targetType: 'generic', draftText: value });
    });
  }, []);

  const discardComposerDraft = useCallback(() => {
    setComposerSession(null);
  }, []);

  const startComposerSession = useCallback(
    (selected: AliceSuggestedAction) => {
      setMode('composer');
      const recordType = currentContext.recordType;
      setComposerSession(
        createComposerSession({
          actionKey: selected.id,
          targetType: defaultComposerTargetForRecord(recordType),
          targetRecordId: currentContext.recordId,
          targetRecordType: recordType,
          contextSnapshot: currentContext,
          draftText: `Draft from Alice: ${selected.label}\n\nContext: ${
            currentContext.recordLabel ?? currentContext.pageTitle
          }\n\n`,
        }),
      );
    },
    [currentContext, setMode],
  );

  const runSuggestion = useCallback(
    (selected: AliceSuggestedAction) => {
      if (selected.mode !== 'composer') return;
      startComposerSession(selected);
    },
    [startComposerSession],
  );

  const value = useMemo<AliceContextValue>(
    () => ({
      config: resolvedConfig,
      currentContext,
      panelState,
      mode,
      suggestions,
      useCurrentContext,
      composerSession,
      composerDraft,
      composerDirty,
      registerAliceContext,
      setPanelState,
      setMode,
      setUseCurrentContext,
      setComposerDraft,
      discardComposerDraft,
      runSuggestion,
    }),
    [
      composerSession,
      composerDirty,
      composerDraft,
      currentContext,
      discardComposerDraft,
      mode,
      panelState,
      registerAliceContext,
      resolvedConfig,
      runSuggestion,
      setComposerDraft,
      setMode,
      suggestions,
      useCurrentContext,
    ],
  );

  return <AliceContext.Provider value={value}>{children}</AliceContext.Provider>;
}

export function useAlice() {
  const value = useContext(AliceContext);
  if (!value) throw new Error('useAlice must be used within AliceProvider');
  return value;
}

export function useAlicePageContext(context: AlicePageContext) {
  const { registerAliceContext } = useAlice();

  useEffect(() => registerAliceContext(context), [context, registerAliceContext]);
}
